// Visual approximation of the email blocks, rendered with React.
// Mirrors HTML_RENDERERS exactly except for the table-based email markup —
// here we use divs/flex for the WYSIWYG preview.

import type { Block, BlockType } from '@shared/types';
import { RichInline } from './inline-md';
import { formatEventDate, todayIso, type EventLike } from '@shared/event-date';
import { storyBodyItems, resolveCalendarEvents } from './blocks';
import { useUpcomingEvents } from './UpcomingEventsContext';

interface PreviewCardProps {
  children: React.ReactNode;
  bg?: string;
  border?: string;
}

const PreviewCard = ({ children, bg = '#FFFFFF', border = '' }: PreviewCardProps) => (
  <div style={{ background: bg, borderRadius: 8, marginBottom: 16, overflow: 'hidden', border }}>{children}</div>
);

interface ForecastDay { day: string; high: string; low: string; icon: string; }
interface BulletinItem { headline: string; body: string; sourceLabel?: string; sourceUrl?: string; }
interface PollOption { label: string; url: string; }
interface Social { label: string; url: string; }

const HeaderPreview = ({ data }: { data: any }) => (
  <PreviewCard>
    <div style={{ background: '#E2F4FF', padding: '28px 36px 20px 36px', textAlign: 'center' }}>
      {data.logoUrl && <img src={data.logoUrl} alt="Penobscot Morning" style={{ maxWidth: 240, height: 'auto', margin: '0 auto', display: 'block' }} onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />}
    </div>
    <div style={{ padding: '16px 36px', display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#5C6370', letterSpacing: 0.4, textTransform: 'uppercase', fontWeight: 600 }} className="font-body">
      <span>{data.dateLine}</span>
      <span>{data.editionMode === 'custom' ? data.editionNumber : `Edition Nº ${data.editionNumber}`}</span>
    </div>
  </PreviewCard>
);

const GreetingPreview = ({ data }: { data: any }) => (
  <PreviewCard>
    <div style={{ padding: '32px 36px' }} className="font-body">
      <p style={{ margin: '0 0 14px 0', fontSize: 17, lineHeight: 1.55, color: '#1A1F2E' }}>
        <strong style={{ color: '#153EA7' }}>{data.lead}</strong> <RichInline md={data.body} />
      </p>
      <p style={{ margin: 0, fontSize: 15, color: '#5C6370', fontStyle: 'italic' }}>— {data.signature}</p>
    </div>
  </PreviewCard>
);

const WeatherPreview = ({ data }: { data: any }) => (
  <PreviewCard>
    <div style={{ padding: '22px 36px 8px 36px' }}>
      <span className="font-body" style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.4, color: '#153EA7', textTransform: 'uppercase' }}>Penobscot at a Glance</span>
    </div>
    <div style={{ padding: '14px 36px 28px 36px' }}>
      <div style={{ background: '#F5FAFE', border: '1px solid #D6E7F4', borderRadius: 6, display: 'flex' }}>
        <div style={{ flex: 1, padding: '18px 16px', borderRight: '1px solid #D6E7F4' }} className="font-body">
          <p style={{ margin: '0 0 6px 0', fontSize: 11, fontWeight: 700, color: '#5C6370', letterSpacing: 0.8, textTransform: 'uppercase' }}>Today</p>
          <p className="font-display" style={{ margin: '0 0 6px 0', fontSize: 36, fontWeight: 600, color: '#153EA7', lineHeight: 1, letterSpacing: -1 }}>{data.todayTemp}</p>
          <p style={{ margin: '0 0 4px 0', fontSize: 13, color: '#1A1F2E', fontWeight: 600 }}>{data.todayIcon} {data.todayLabel}</p>
          <p style={{ margin: 0, fontSize: 12, color: '#5C6370' }}>High {data.todayHigh} · Low {data.todayLow}</p>
        </div>
        <div style={{ flex: 1, padding: '18px 16px', borderRight: '1px solid #D6E7F4' }} className="font-body">
          <p style={{ margin: '0 0 6px 0', fontSize: 11, fontWeight: 700, color: '#5C6370', letterSpacing: 0.8, textTransform: 'uppercase' }}>Tomorrow</p>
          <p className="font-display" style={{ margin: '0 0 6px 0', fontSize: 30, fontWeight: 600, color: '#153EA7', lineHeight: 1, letterSpacing: -1 }}>{data.tomorrowTemp}</p>
          <p style={{ margin: '0 0 4px 0', fontSize: 13, color: '#1A1F2E', fontWeight: 600 }}>{data.tomorrowIcon} {data.tomorrowLabel}</p>
          <p style={{ margin: 0, fontSize: 12, color: '#5C6370' }}>High {data.tomorrowHigh} · Low {data.tomorrowLow}</p>
        </div>
        <div style={{ flex: 1, padding: '18px 16px' }} className="font-body">
          <p style={{ margin: '0 0 8px 0', fontSize: 11, fontWeight: 700, color: '#5C6370', letterSpacing: 0.8, textTransform: 'uppercase' }}>Rest of Week</p>
          {((data.forecast as ForecastDay[]) || []).map((f, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', fontSize: 12, color: '#1A1F2E' }}>
              <strong style={{ color: '#5C6370', fontWeight: 600 }}>{f.day}</strong>
              <span>{f.high}/{f.low} {f.icon}</span>
            </div>
          ))}
        </div>
      </div>
      {(data.sunrise || data.sunset) && (
        <p className="font-body" style={{ margin: '14px 0 0 0', fontSize: 13, color: '#5C6370' }}>
          {data.sunrise && <><strong style={{ color: '#1A1F2E' }}>Sunrise:</strong> {data.sunrise}</>}
          {data.sunrise && data.sunset && <span style={{ color: '#A0AEC0', margin: '0 8px' }}>·</span>}
          {data.sunset && <><strong style={{ color: '#1A1F2E' }}>Sunset:</strong> {data.sunset}</>}
        </p>
      )}
      {data.notes && (
        <p className="font-body" style={{ margin: '14px 0 0 0', fontSize: 14, lineHeight: 1.55, color: '#1A1F2E' }}>
          <RichInline md={data.notes} />
        </p>
      )}
    </div>
  </PreviewCard>
);

const StoryPreview = ({ data }: { data: any }) => {
  const body = storyBodyItems(data);
  const paragraphIndices = body.map((b, i) => (b.type === 'paragraph' ? i : -1)).filter((i) => i >= 0);
  const lastParagraphIdx = paragraphIndices[paragraphIndices.length - 1] ?? -1;

  return (
    <PreviewCard>
      {data.imageUrl && <img src={data.imageUrl} alt={data.imageAlt} style={{ width: '100%', display: 'block' }} onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />}
      <div style={{ padding: '26px 36px 8px 36px' }}>
        {data.eyebrow && <p className="font-body" style={{ margin: '0 0 10px 0', fontSize: 11, fontWeight: 700, color: '#153EA7', letterSpacing: 1.4, textTransform: 'uppercase' }}>{data.eyebrow}</p>}
        <h1 className="font-display" style={{ margin: '0 0 14px 0', fontSize: 34, lineHeight: 1.15, fontWeight: 700, color: '#1A1F2E', letterSpacing: -0.5 }}>{data.headline}</h1>
        {data.caption && <p className="font-body" style={{ margin: '0 0 18px 0', fontSize: 12, color: '#5C6370', fontStyle: 'italic' }}><RichInline md={data.caption} /></p>}
      </div>
      <div style={{ padding: '0 36px 32px 36px' }} className="font-body">
        {body.map((item, i) => {
          if (item.type === 'image') {
            if (!item.url) return null;
            return (
              <figure key={item.id} style={{ margin: '0 0 16px 0' }}>
                <img src={item.url} alt={item.alt} style={{ width: '100%', display: 'block', borderRadius: 4 }} onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.3'; }} />
                {item.caption && (
                  <figcaption style={{ marginTop: 6, fontSize: 12, color: '#5C6370', fontStyle: 'italic' }}>
                    <RichInline md={item.caption} />
                  </figcaption>
                )}
              </figure>
            );
          }
          const isLastParagraph = i === lastParagraphIdx;
          const isLastItem = i === body.length - 1;
          const showInline = isLastParagraph && data.readMoreUrl && data.readMoreLabel && data.readMoreStyle !== 'button';
          return (
            <p key={item.id} style={{ margin: isLastItem ? 0 : '0 0 16px 0', fontSize: 16, lineHeight: 1.65, color: '#1A1F2E' }}>
              {item.lead && <strong>{item.lead} </strong>}
              <RichInline md={item.body} />
              {showInline && (
                <> <a href={data.readMoreUrl} style={{ color: '#153EA7', textDecoration: 'underline' }}>{data.readMoreLabel}</a>.</>
              )}
            </p>
          );
        })}
        {data.readMoreStyle === 'button' && data.readMoreUrl && data.readMoreLabel && (
          <div style={{ marginTop: 18 }}>
            <span style={{ display: 'inline-block', padding: '10px 22px', background: '#153EA7', borderRadius: 4, color: '#FFFFFF', fontSize: 14, fontWeight: 600 }}>
              {data.readMoreLabel} →
            </span>
          </div>
        )}
      </div>
    </PreviewCard>
  );
};

const SponsorPreview = ({ data }: { data: any }) => (
  <PreviewCard bg="#FFF8F2" border="1px solid #F4E0CC">
    <div style={{ padding: '24px 32px' }} className="font-body">
      <p style={{ margin: '0 0 8px 0', fontSize: 10, fontWeight: 700, color: '#A05A2C', letterSpacing: 1.4, textTransform: 'uppercase' }}>Together with {data.sponsorName}</p>
      <p className="font-display" style={{ margin: '0 0 10px 0', fontSize: 22, fontWeight: 600, color: '#1A1F2E', lineHeight: 1.25 }}>{data.headline}</p>
      <p style={{ margin: '0 0 16px 0', fontSize: 15, lineHeight: 1.55, color: '#3A3F4A' }}><RichInline md={data.body} /></p>
      <span style={{ display: 'inline-block', padding: '10px 22px', background: '#153EA7', borderRadius: 4, color: '#FFFFFF', fontSize: 14, fontWeight: 600 }}>{data.ctaLabel} →</span>
    </div>
  </PreviewCard>
);

const BulletinPreview = ({ data }: { data: any }) => (
  <PreviewCard>
    <div style={{ padding: '26px 36px 6px 36px' }} className="font-body">
      <p style={{ margin: '0 0 6px 0', fontSize: 11, fontWeight: 700, color: '#153EA7', letterSpacing: 1.4, textTransform: 'uppercase' }}>{data.title}</p>
      {data.subtitle && <p style={{ margin: '0 0 8px 0', fontSize: 14, color: '#5C6370', fontStyle: 'italic' }}><RichInline md={data.subtitle} /></p>}
    </div>
    <div style={{ padding: '0 36px 26px 36px' }} className="font-body">
      {((data.items as BulletinItem[]) || []).map((item, i, arr) => (
        <div key={i} style={{ borderBottom: i === arr.length - 1 ? 'none' : '1px solid #EAEEF2', padding: i === arr.length - 1 ? '14px 0 0 0' : '14px 0' }}>
          <p style={{ margin: 0, fontSize: 15, lineHeight: 1.5, color: '#1A1F2E' }}>
            <strong>{item.headline}:</strong> <RichInline md={item.body} />
            {item.sourceUrl && item.sourceLabel && <> <a href={item.sourceUrl} style={{ color: '#153EA7', textDecoration: 'underline' }}>{item.sourceLabel}</a></>}
          </p>
        </div>
      ))}
    </div>
  </PreviewCard>
);

interface PrevResult { label: string; percent: number }

const PollPreview = ({ data }: { data: any }) => {
  const prev = (data.previousResults as PrevResult[]) || [];
  const showPrev = !!(data.previousQuestion && prev.length > 0);
  return (
    <PreviewCard bg="#E2F4FF">
      <div style={{ padding: '26px 32px' }} className="font-body">
        <p style={{ margin: '0 0 8px 0', fontSize: 11, fontWeight: 700, color: '#153EA7', letterSpacing: 1.4, textTransform: 'uppercase' }}>Reader Poll</p>
        <p className="font-display" style={{ margin: '0 0 18px 0', fontSize: 22, fontWeight: 600, color: '#1A1F2E', lineHeight: 1.25 }}>{data.question}</p>
        {((data.options as PollOption[]) || []).map((opt, i) => (
          <div key={i} style={{ padding: '13px 16px', background: '#FFFFFF', border: '1px solid #C9E2F2', borderRadius: 4, color: '#153EA7', fontSize: 15, fontWeight: 600, marginBottom: 10 }}>
            → {opt.label}
          </div>
        ))}
        {showPrev && (
          <div style={{ marginTop: 22, paddingTop: 22, borderTop: '1px dashed #C9E2F2' }}>
            <p style={{ margin: '0 0 6px 0', fontSize: 10, fontWeight: 700, color: '#5C6370', letterSpacing: 1.4, textTransform: 'uppercase' }}>{data.previousLabel || "Yesterday's poll"}</p>
            <p style={{ margin: '0 0 12px 0', fontSize: 14, color: '#1A1F2E', fontStyle: 'italic' }}>{data.previousQuestion}</p>
            {prev.map((r, i) => (
              <div key={i} style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#1A1F2E', marginBottom: 3 }}>
                  <span>{r.label || `Option ${i + 1}`}</span>
                  <span style={{ fontWeight: 600 }}>{r.percent}%</span>
                </div>
                <div style={{ background: '#FFFFFF', height: 6, borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ background: '#153EA7', height: '100%', width: `${Math.max(0, Math.min(100, r.percent))}%` }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </PreviewCard>
  );
};

interface CalendarEventData extends EventLike {
  name?: string;
  description?: string;
  linkLabel?: string;
  linkUrl?: string;
  // Old shape — fall back to these if present.
  url?: string;
}

const CalendarPreview = ({ data }: { data: any }) => {
  const dbEvents = useUpcomingEvents();
  const events = resolveCalendarEvents(data, dbEvents) as CalendarEventData[];
  const today = todayIso();
  return (
    <PreviewCard>
      <div style={{ padding: '26px 36px 6px 36px' }} className="font-body">
        <p style={{ margin: '0 0 6px 0', fontSize: 11, fontWeight: 700, color: '#153EA7', letterSpacing: 1.4, textTransform: 'uppercase' }}>{data.title}</p>
        {data.subtitle && <p style={{ margin: '0 0 8px 0', fontSize: 14, color: '#5C6370', fontStyle: 'italic' }}>{data.subtitle}</p>}
      </div>
      <div style={{ padding: '0 36px 26px 36px' }} className="font-body">
        {events.length === 0 && <p style={{ fontSize: 13, color: '#A0AEC0', fontStyle: 'italic', padding: '14px 0' }}>No events yet.</p>}
        {events.map((ev, i, arr) => {
          const isLast = i === arr.length - 1;
          const when = formatEventDate(ev, today);
          const url = ev.linkUrl || ev.url || '';
          const label = ev.linkLabel || '';
          const showLink = !!(url && label);
          return (
            <div key={i} style={{ borderBottom: isLast ? 'none' : '1px solid #EAEEF2', padding: isLast ? '14px 0 0 0' : '14px 0' }}>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.5, color: '#1A1F2E' }}>
                <strong>{when}:</strong> {ev.name}
                {ev.description && <> {ev.description}</>}
                {showLink && <> <a href={url} style={{ color: '#153EA7', textDecoration: 'underline' }}>{label} →</a></>}
              </p>
            </div>
          );
        })}
      </div>
    </PreviewCard>
  );
};

const SignoffPreview = ({ data }: { data: any }) => (
  <PreviewCard>
    <div style={{ padding: '28px 36px 24px 36px' }} className="font-body">
      <p style={{ margin: '0 0 6px 0', fontSize: 15, color: '#1A1F2E' }}><RichInline md={data.closing} /></p>
      <p className="font-display" style={{ margin: '0 0 22px 0', fontSize: 18, fontWeight: 600, color: '#153EA7', fontStyle: 'italic' }}>{data.signature}</p>
      <div style={{ borderTop: '1px dashed #C9E2F2', paddingTop: 22, textAlign: 'center' }}>
        <p style={{ margin: '0 0 12px 0', fontSize: 14, color: '#5C6370' }}>{data.forwardPrompt}</p>
        <span style={{ display: 'inline-block', padding: '9px 20px', background: '#FFFFFF', border: '1.5px solid #153EA7', borderRadius: 4, color: '#153EA7', fontSize: 14, fontWeight: 600 }}>{data.forwardLabel}</span>
      </div>
    </div>
  </PreviewCard>
);

const FooterPreview = ({ data }: { data: any }) => (
  <div style={{ padding: '12px 36px 24px 36px', textAlign: 'center' }} className="font-body">
    {(data.editorEmail || data.editorPhone) && (
      <p style={{ margin: '0 0 14px 0', fontSize: 12, color: '#5C6370' }}>
        <strong style={{ color: '#1A1F2E' }}>Reach the editor:</strong>{' '}
        {data.editorEmail && <span style={{ color: '#153EA7' }}>{data.editorEmail}</span>}
        {data.editorEmail && data.editorPhone && <span style={{ color: '#A0AEC0', margin: '0 6px' }}>·</span>}
        {data.editorPhone && <span style={{ color: '#153EA7' }}>{data.editorPhone}</span>}
      </p>
    )}
    <div style={{ marginBottom: 18 }}>
      {((data.socials as Social[]) || []).map((s, i) => (
        <span key={i}>
          {i > 0 && <span style={{ color: '#A0AEC0', margin: '0 4px' }}>·</span>}
          <span style={{ color: '#153EA7', fontSize: 13, fontWeight: 600, padding: '0 8px' }}>{s.label}</span>
        </span>
      ))}
    </div>
    <p style={{ margin: '0 0 6px 0', fontSize: 12, color: '#5C6370' }}><RichInline md={data.tagline} /></p>
    <p style={{ margin: '0 0 14px 0', fontSize: 12, color: '#8A929E' }}>{data.address}</p>
    <p style={{ margin: 0, fontSize: 12, color: '#8A929E' }}>
      <span style={{ color: '#5C6370', textDecoration: 'underline' }}>Unsubscribe</span>
      &nbsp;·&nbsp;
      <span style={{ color: '#5C6370', textDecoration: 'underline' }}>Web version</span>
    </p>
  </div>
);

export const PREVIEW_COMPONENTS: Record<BlockType, React.ComponentType<{ data: any }>> = {
  header: HeaderPreview,
  greeting: GreetingPreview,
  weather: WeatherPreview,
  story: StoryPreview,
  sponsor: SponsorPreview,
  bulletin: BulletinPreview,
  calendar: CalendarPreview,
  poll: PollPreview,
  signoff: SignoffPreview,
  footer: FooterPreview,
};

export function BlockPreview({ block }: { block: Block }) {
  const Component = PREVIEW_COMPONENTS[block.type];
  if (!Component) return null;
  return <Component data={block.data} />;
}
